/**
 * Web Push Notification Service for trainedbyRS
 * Uses the browser Push API + Service Worker (no Firebase).
 */
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Convert URL-safe base64 to Uint8Array (required by PushManager.subscribe)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

class WebPushService {
  constructor() {
    this.swRegistration = null;
    this.subscription = null;
    this._initPromise = null;
  }

  /** Check if Web Push is supported */
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  /** Get current permission: 'granted' | 'denied' | 'default' */
  getPermission() {
    if (!('Notification' in window)) return 'denied';
    return Notification.permission;
  }

  /** Initialize — register SW and check existing subscription. Safe to call multiple times. */
  async init() {
    if (this._initPromise) return this._initPromise;
    this._initPromise = this._doInit();
    return this._initPromise;
  }

  async _doInit() {
    if (!this.isSupported()) return false;
    try {
      // Use the existing SW registration (registered in index.js) or register new
      this.swRegistration = await navigator.serviceWorker.ready;
      this.subscription = await this.swRegistration.pushManager.getSubscription();
      return true;
    } catch (e) {
      console.error('[WebPush] init failed:', e);
      return false;
    }
  }

  /** Is user currently subscribed to push? */
  isSubscribed() {
    return !!this.subscription;
  }

  /** Request permission and subscribe to push notifications. Returns { success, error } */
  async subscribe() {
    if (!this.swRegistration) {
      const ok = await this.init();
      if (!ok) return { success: false, error: 'Service worker not available' };
    }

    // Request permission
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return { success: false, error: permission === 'denied' ? 'blocked' : 'dismissed' };
      }
    } catch (e) {
      return { success: false, error: 'Permission request failed' };
    }

    // Get VAPID public key from backend
    let vapidKey;
    try {
      const { data } = await axios.get(`${API}/notifications/vapid-public-key`);
      if (!data.public_key) throw new Error('Empty VAPID key');
      vapidKey = urlBase64ToUint8Array(data.public_key);
    } catch (e) {
      console.error('[WebPush] VAPID key fetch failed:', e);
      return { success: false, error: 'Could not fetch push key from server' };
    }

    // Subscribe via Push API — unsubscribe first if there's a stale subscription with different keys
    try {
      const existingSub = await this.swRegistration.pushManager.getSubscription();
      if (existingSub) {
        try {
          await existingSub.unsubscribe();
          console.log('[WebPush] Cleared old subscription before re-subscribing');
        } catch (unsErr) {
          console.warn('[WebPush] Failed to clear old subscription:', unsErr);
        }
      }
      this.subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey,
      });
    } catch (e) {
      console.error('[WebPush] pushManager.subscribe failed:', e);
      // Last resort: try unregistering SW and re-registering
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const reg of regs) await reg.unregister();
        const newReg = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
        this.swRegistration = newReg;
        this.subscription = await newReg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidKey,
        });
        console.log('[WebPush] Recovered with fresh SW registration');
      } catch (retryErr) {
        console.error('[WebPush] Retry also failed:', retryErr);
        return { success: false, error: `Browser push subscription failed: ${e.message}` };
      }
    }

    // Send subscription to backend
    try {
      const subJSON = this.subscription.toJSON();
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/notifications/push-subscribe`, {
        endpoint: subJSON.endpoint,
        keys: {
          p256dh: subJSON.keys.p256dh,
          auth: subJSON.keys.auth,
        },
        user_agent: navigator.userAgent,
      }, { headers });
    } catch (e) {
      console.error('[WebPush] Backend subscribe failed:', e);
      return { success: false, error: 'Saved in browser but failed to register with server' };
    }

    return { success: true };
  }

  /** Unsubscribe from push notifications */
  async unsubscribe() {
    try {
      if (this.subscription) {
        const endpoint = this.subscription.endpoint;
        await this.subscription.unsubscribe();
        this.subscription = null;
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.post(`${API}/notifications/push-unsubscribe`, { endpoint }, { headers }).catch(() => {});
      }
      return true;
    } catch (e) {
      console.error('[WebPush] unsubscribe failed:', e);
      return false;
    }
  }
}

const webPushService = new WebPushService();
export default webPushService;
